import { useState } from "react";
import { Helmet } from "react-helmet";
import MonoButton from "../kits/DesignKit/MonoButton";
import { usePlaceholder } from "../context/usePlaceholder";
import { buildFAQData } from "./utils/seoSchema";
import { logoPath } from "../config";



// Programmliste
const programList = {
  "Eigene Anfrage": ["ich habe eine eigene Anfrage jetzt"],
  "2D": ["Adobe Illustrator", "Photoshop", "AfterEffects"],
  "3D": ["Blender", "Cinema 4D", "Rendering", "Scripting Python"],
  "CODE": ["React JS", "Angular", "Next.js", "Three.js"],
  "MEDIA": ["Videoschnitt Premiere", "Videobearbeitung AfterEffects", "FinalCut"],
  "Web Entwicklung": ["interaktives Web App", "eigene Webentwicklung"]
};

function CustomCourseRequest({ onClose, autoOpen = false }) {
  const [step, setStep] = useState(autoOpen ? "kategorie" : "intro");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [consentGiven, setConsentGiven] = useState(false);

  const {
    namePlaceholder,
    emailPlaceholder,
    messagePlaceholder,
    nameSinglePlaceholder
  } = usePlaceholder();

  // Form submit
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentGiven) return;
    const payload = {
      category: selectedCategory,
      program: selectedProgram,
      program2: selectedProgram,
      ...formData
    };
    try {
      const response = await fetch("https://www.denisbitter.de/stage01/api/CustomCourseRequest.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (response.ok) setStep("danke");
      else console.error("Anfrage fehlgeschlagen");
    } catch (error) {
      console.error("Fehler beim Senden der Anfrage:", error);
    }
  };

  // Modal-Inhalt nach Schritt
  let modalContent = null;
  if (step === "intro") {
    modalContent = (
      <>
        <p className="text-gray-800 text-center text-xs sm:text-sm font-mono mb-6">
          Du stehst mittendrin in ein Projekt {nameSinglePlaceholder}?<br />
          Dann sag mir einfach, was dir fehlt. <br />
          Lass uns zusammen schauen, wie ich dir helfen kann!
        </p>
        <MonoButton
          onClick={() => setStep("kategorie")}
          className="text-xs text-center w-full"
        >
          Ich würde gerne Dich anfragen
        </MonoButton>
      </>
    );
  }
  else if (step === "kategorie") {
    modalContent = (
      <>
        <p className="text-center text-xs text-gray-600 mb-1">Schritt 01</p>
        <p className="text-center text-xs font-semibold text-gray-800 mb-4">
          Wobei brauchst du Unterstützung?{" "}
          <span className="font-mono text-xs text-gray-500">{nameSinglePlaceholder}</span>
        </p>
        <div className="flex flex-col gap-3">
          {Object.keys(programList).map((cat) => (
            <MonoButton
              key={cat}
              onClick={() => { setSelectedCategory(cat); setStep("programm"); }}
              className={`w-full text-xs font-mono ${selectedCategory === cat ? "bg-gray-950 text-orange-50" : ""}`}
            >
              {cat}
            </MonoButton>
          ))}
        </div>
      </>
    );
  }
  else if (step === "programm") {
    modalContent = (
      <>
        <p className="text-gray-600 text-xs mb-2">Schritt 02</p>
        <p className="text-gray-600 text-xs font-semibold mb-2">
          Welches Programm interessiert dich?
        </p>
        <div className="flex flex-col gap-3 mb-2">
          {programList[selectedCategory].map((prog) => (
            <MonoButton
              key={prog}
              onClick={() => { setSelectedProgram(prog); setStep("form"); }}
              className="w-full text-xs"
            >
              {prog}
            </MonoButton>
          ))}
        </div>
        <MonoButton
          className="w-full text-xs mt-2"
          onClick={() => setStep("kategorie")}
        >
          Kategorie wechseln
        </MonoButton>
      </>
    );
  }
  else if (step === "form") {
    modalContent = (
      <form
        className="flex flex-col gap-3 mx-auto"
        key="form"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-3 pt-2 text-center">
          <p className="text-xs">Schritt 03</p>
          <p className="text-xs font-semibold">Wie kann ich dich erreichen?</p>
          <p className="text-gray-700 text-xs">
            {nameSinglePlaceholder}, du hast gewählt:<br />
            <strong>
              {selectedCategory ? `${selectedCategory} – ${selectedProgram}` : `Individuell – ${selectedProgram}`}
            </strong>
          </p>
          <input
            type="text"
            name="name"
            placeholder={namePlaceholder}
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full border rounded px-4 py-2 text-xs outline-none focus:ring-1 focus:ring-gray-900"
          />
          <input
            type="email"
            name="email"
            placeholder={emailPlaceholder}
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full border rounded px-4 py-2 text-xs outline-none focus:ring-1 focus:ring-gray-900"
          />
          <textarea
            name="message"
            placeholder={messagePlaceholder}
            value={formData.message}
            onChange={handleInputChange}
            className="w-full border rounded px-4 py-2 text-xs outline-none focus:ring-1 focus:ring-gray-900"
            rows={4}
          ></textarea>
          {/* DSGVO Checkbox */}
          <div className="flex items-start text-left text-xs text-gray-600 gap-2">
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              required
              className="mt-0.5 shrink-0 appearance-none w-4 h-4 border border-gray-400 rounded-sm checked:bg-black checked:border-black focus:outline-none focus:ring-1 focus:ring-black"
            />
            <label className="leading-snug">
              Ich stimme zu, dass meine Daten zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden.{" "}
              <a href="/datenschutz" className="underline" target="_blank" rel="noreferrer"><br /> Mehr erfahren</a>.
            </label>
          </div>
          <MonoButton
            type="submit"
            className={`w-full text-xs ${!consentGiven ? "opacity-10 pointer-events-none" : ""}`}
          >
            Anfrage an Denis senden
          </MonoButton>
          <MonoButton
            type="button"
            className="w-full text-xs text-gray-500"
            onClick={() => {
              setFormData({ name: "", email: "", message: "" });
              setConsentGiven(false);
              setSelectedProgram(null);
              setSelectedCategory(null);
              setStep("kategorie");
            }}
          >
            Zurück zur Auswahl
          </MonoButton>
        </div>
      </form>
    );
  }
  else if (step === "danke") {
    modalContent = (
      <div className="relative space-y-4">
        <div className="w-14 h-14  mx-auto rounded-full shadow-lg  bg-gradient-to-br from-gray-100 via-white to-gray-100/50  flex items-center justify-center ring-1 ring-gray-200">
          <img src={logoPath} alt="Logo" className="w-18 h-18  object-contain  rounded-full" loading="lazy" />
        </div>
        <div className="flex flex-col gap-3 items-center justify-center text-center min-h-[200px]">
          <p className="text-sm font-semibold text-black">
            Danke, {formData.name || nameSinglePlaceholder}!
          </p>
          <p className="text-xs text-gray-500">
            Ich melde mich innerhalb eines Tages <br /> per E-Mail bei dir.
          </p>
          <p className="text-xs">Oder schneller, wenn Du mich anrufst: <br />
            <a href="tel:+4915153231791" className="text-brand hover:underline">0151 5323 1791</a> <br />
            <p className="text-xs"> Danke, bis später, Denis</p>
           </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildFAQData(programList))}
        </script>
      </Helmet>
      {modalContent}
    </>
  );
}

export default CustomCourseRequest;
